import { CSSProperties, useRef } from "react";
import { ArrowUpRight } from "@phosphor-icons/react";
import { gsap, useGSAP, MOTION_OK, sel } from "../lib/motion";
import { projects } from "../data/projects";
import PhoneShowcase, { PhoneFan } from "./PhoneShowcase";
import s from "./Projects.module.css";

/** Must match the media query that turns the cards sticky in Projects.module.css. */
const STACK_QUERY = `(min-width: 960px) and (min-height: 700px) and ${MOTION_OK}`;

export default function Projects() {
  const root = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();

      // Sticky stack: CSS pins each card; as the next card arrives, the one beneath recedes.
      mm.add(STACK_QUERY, () => {
        const cards = gsap.utils.toArray<HTMLElement>(sel(s.card));
        cards.forEach((card, i) => {
          if (i === cards.length - 1) return;
          gsap.to(card.firstElementChild, {
            scale: 0.93,
            transformOrigin: "50% 0%",
            ease: "none",
            scrollTrigger: { trigger: cards[i + 1], start: "top bottom", end: "top 18%", scrub: true },
          });
        });
      });

      mm.add(MOTION_OK, () => {
        gsap.from(sel(s.intro), {
          autoAlpha: 0,
          y: 20,
          duration: 0.9,
          ease: "expo.out",
          scrollTrigger: { trigger: sel(s.intro), start: "top 88%", once: true },
        });
      });
    },
    { scope: root }
  );

  return (
    <section ref={root} id="work" className={`section ${s.work}`} aria-labelledby="work-title" tabIndex={-1}>
      <div className="container">
        <h2 id="work-title" className="section-title">
          Selected work
        </h2>
        <p className={`lede ${s.intro}`}>Products I designed, built or led, shown with their real screens.</p>

        <div className={s.stack}>
          {projects.map((project, i) => (
            <article key={project.id} className={s.card} style={{ "--i": i } as CSSProperties}>
              <div className={s.cardInner}>
                <div className={s.text}>
                  <h3 className={s.name}>{project.name}</h3>
                  <p className={s.kind}>{project.kindLabel}</p>
                  <p className={s.blurb}>{project.blurb}</p>
                  <ul className={s.highlights}>
                    {project.highlights.slice(0, 3).map((h) => (
                      <li key={h}>{h}</li>
                    ))}
                  </ul>
                  <ul className="tag-list" aria-label="Technologies">
                    {project.stack.map((t) => (
                      <li key={t} className="tag">
                        {t}
                      </li>
                    ))}
                  </ul>
                  {(project.url || project.stores || project.extraLink) && (
                    <div className={s.links}>
                      {project.url && (
                        <a href={project.url} target="_blank" rel="noreferrer" className={s.link}>
                          {project.kind === "fan" ? "Google Play" : "Visit site"}
                          <ArrowUpRight size={16} weight="bold" aria-hidden="true" />
                        </a>
                      )}
                      {project.stores?.apple && (
                        <a href={project.stores.apple} target="_blank" rel="noreferrer" className={s.link}>
                          App Store
                          <ArrowUpRight size={16} weight="bold" aria-hidden="true" />
                        </a>
                      )}
                      {project.stores?.play && (
                        <a href={project.stores.play} target="_blank" rel="noreferrer" className={s.link}>
                          Google Play
                          <ArrowUpRight size={16} weight="bold" aria-hidden="true" />
                        </a>
                      )}
                      {project.extraLink && (
                        <a href={project.extraLink.href} target="_blank" rel="noreferrer" className={s.link}>
                          {project.extraLink.label}
                          <ArrowUpRight size={16} weight="bold" aria-hidden="true" />
                        </a>
                      )}
                    </div>
                  )}
                </div>

                <div className={s.media}>
                  {project.kind === "fan" ? <PhoneFan project={project} /> : <PhoneShowcase project={project} />}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
